import {
	BuilderToSubmissionFormData,
	DecennialCertificatePayload,
	DesignerToSubmissionFormData,
	MaterialToSubmissionFormData,
	ParticipantToConstructionSiteSubmission,
	ParticipantToSubmissionFormMode,
	SubmissionParticipantWithValidDecennialCertificate,
	builderToSubmissionFormSchema,
	designerToSubmissionFormSchema,
	materialToSubmissionFormSchema,
} from "@dune/types";
import {
	architecteMissions,
	builderMainActivities,
	bureauEtudeMissions,
	controleurTechniqueMissions,
	geotechnicienMissions,
	maitreOeuvreMissions,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
	useGetConstructionSiteSubmissionOperation,
	useGetParticipantToConstructionSiteSubmission,
} from "../api/queries/constructionSiteSubmission.queries";

const filterAllowed = <T extends readonly string[]>(
	values: string[] | undefined | null,
	allowed: T,
): T[number][] => {
	if (!values) return [];
	return values.filter((v): v is T[number] => allowed.includes(v));
};

type ParticipantToSubmissionData = ParticipantToConstructionSiteSubmission & {
	decennialCertificatesOfSubmissionOrValidFromOthers: DecennialCertificatePayload[];
};

interface UseDesignerToSubmissionFormProps {
	mode: ParticipantToSubmissionFormMode;
	onSubmit: (data: DesignerToSubmissionFormData) => void;
	constructionSiteId?: number;
	participantId?: number;
	prefillFrom?: SubmissionParticipantWithValidDecennialCertificate | null;
}

export const useDesignerToSubmissionForm = ({
	onSubmit,
	constructionSiteId,
	participantId,
	prefillFrom,
}: UseDesignerToSubmissionFormProps) => {
	const shouldFetchParticipant =
		!prefillFrom && !!constructionSiteId && !!participantId;
	const { data: participantToSubmission } =
		useGetParticipantToConstructionSiteSubmission(
			shouldFetchParticipant ? constructionSiteId : 0,
			shouldFetchParticipant ? participantId : 0,
		);

	const schema = designerToSubmissionFormSchema;
	const form = useForm({
		resolver: zodResolver(schema),
		mode: "onChange",
		defaultValues: {
			denomination: "",
			totalFeesTTC: 0,
			totalFeesHT: 0,
			feeAgreementsFilesIds: [],
			decennialCertificatesFilesIds: [],
			architecteMissions: [],
			maitreOeuvreMissions: [],
			geotechnicienMissions: [],
			controleurTechniqueMissions: [],
			bureauEtudeMissions: [],
			city: "",
			country: "",
			registrationNumber: "",
			siren: "",
			streetName: "",
			streetNumber: "",
			postalCode: "",
			designerType: undefined,
			missions: undefined,
		},
	});

	const handleSubmit = form.handleSubmit(onSubmit);

	const resetFormData = useCallback(
		(participantData: ParticipantToSubmissionData) => {
			const designerType = participantData.designerType;
			const missions = participantData.totalMissionsOrActivitiesOrMaterialTypes;
			const decennialCertificatesFilesIds = [
				...participantData.participant.filesOnConstructionSiteSubmission
					.filter((file) => file.type === "ATTESTATION_DECENNALE")
					.map((file) => file.file.id),
				...participantData.decennialCertificatesOfSubmissionOrValidFromOthers.map(
					(decennialCertificate) => decennialCertificate.file.id,
				),
			];

			form.reset({
				denomination:
					participantData.participant.society?.denomination ??
					participantData.participant.foreignSociety?.denomination ??
					"",
				totalFeesTTC: participantData.totalFeesTTC,
				totalFeesHT: participantData.totalFeesHT,
				feeAgreementsFilesIds:
					participantData.participant.filesOnConstructionSiteSubmission
						.filter((file) => file.type === "CONVENTION_HONORAIRES")
						.map((file) => file.file.id),
				decennialCertificatesFilesIds,
				designerType: designerType ?? undefined,
				missions: undefined,
				city:
					participantData.participant.society?.headOfficeAddress?.city ?? "",
				streetName:
					participantData.participant.society?.headOfficeAddress?.streetName ??
					"",
				streetNumber:
					participantData.participant.society?.headOfficeAddress
						?.streetNumber ?? "",
				postalCode:
					participantData.participant.society?.headOfficeAddress?.postalCode ??
					"",
				country: participantData.participant.foreignSociety?.country ?? "",
				registrationNumber:
					participantData.participant.foreignSociety?.registrationNumber ?? "",
				architecteMissions:
					designerType === "ARCHITECTE"
						? filterAllowed(missions, architecteMissions)
						: [],
				maitreOeuvreMissions:
					designerType === "MAITRE_OEUVRE"
						? filterAllowed(missions, maitreOeuvreMissions)
						: [],
				geotechnicienMissions:
					designerType === "GEOTECHNICIEN"
						? filterAllowed(missions, geotechnicienMissions)
						: [],
				controleurTechniqueMissions:
					designerType === "CONTROLEUR_TECHNIQUE"
						? filterAllowed(missions, controleurTechniqueMissions)
						: [],
				bureauEtudeMissions:
					designerType === "BUREAU_ETUDE"
						? filterAllowed(missions, bureauEtudeMissions)
						: [],
				siren: participantData.participant.society?.siren ?? undefined,
			});
		},
		[form],
	);

	const resetFormDataFromReception = useCallback(
		(participantData: SubmissionParticipantWithValidDecennialCertificate) => {
			const designerType = participantData.designerType;
			const missions = participantData.totalMissionsOrActivitiesOrMaterialTypes;

			form.reset({
				denomination:
					participantData.participant.society?.denomination ??
					participantData.participant.foreignSociety?.denomination ??
					"",
				totalFeesTTC: participantData.totalFeesTTC ?? 0,
				totalFeesHT: participantData.totalFeesHT ?? 0,
				feeAgreementsFilesIds: [],
				decennialCertificatesFilesIds: [],
				designerType: designerType ?? undefined,
				missions: undefined,
				city:
					participantData.participant.society?.headOfficeAddress?.city ?? "",
				streetName:
					participantData.participant.society?.headOfficeAddress?.streetName ??
					"",
				streetNumber:
					participantData.participant.society?.headOfficeAddress
						?.streetNumber ?? "",
				postalCode:
					participantData.participant.society?.headOfficeAddress?.postalCode ??
					"",
				country: participantData.participant.foreignSociety?.country ?? "",
				registrationNumber:
					participantData.participant.foreignSociety?.registrationNumber ?? "",
				architecteMissions:
					designerType === "ARCHITECTE"
						? filterAllowed(missions, architecteMissions)
						: [],
				maitreOeuvreMissions:
					designerType === "MAITRE_OEUVRE"
						? filterAllowed(missions, maitreOeuvreMissions)
						: [],
				geotechnicienMissions:
					designerType === "GEOTECHNICIEN"
						? filterAllowed(missions, geotechnicienMissions)
						: [],
				controleurTechniqueMissions:
					designerType === "CONTROLEUR_TECHNIQUE"
						? filterAllowed(missions, controleurTechniqueMissions)
						: [],
				bureauEtudeMissions:
					designerType === "BUREAU_ETUDE"
						? filterAllowed(missions, bureauEtudeMissions)
						: [],
				siren: participantData.participant.society?.siren ?? undefined,
			});
		},
		[form],
	);

	useEffect(() => {
		if (participantToSubmission) {
			resetFormData(participantToSubmission);
			return;
		}
		if (prefillFrom) {
			resetFormDataFromReception(prefillFrom);
		}
	}, [
		participantToSubmission,
		resetFormData,
		prefillFrom,
		resetFormDataFromReception,
	]);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

// Hook pour Builder
interface UseBuilderToSubmissionFormProps {
	mode: ParticipantToSubmissionFormMode;
	onSubmit: (data: BuilderToSubmissionFormData) => void;
	constructionSiteId?: number;
	participantId?: number;
	prefillFrom?: SubmissionParticipantWithValidDecennialCertificate | null;
}

export const useBuilderToSubmissionForm = ({
	onSubmit,
	constructionSiteId,
	participantId,
	prefillFrom,
}: UseBuilderToSubmissionFormProps) => {
	const shouldFetchParticipant =
		!prefillFrom && !!constructionSiteId && !!participantId;

	const { data: participantToSubmission } =
		useGetParticipantToConstructionSiteSubmission(
			shouldFetchParticipant ? constructionSiteId : 0,
			shouldFetchParticipant ? participantId : 0,
		);
	const form = useForm({
		resolver: zodResolver(builderToSubmissionFormSchema),
		defaultValues: {
			denomination: "",
			totalFeesTTC: 0,
			totalFeesHT: 0,
			quotesFilesIds: [],
			decennialCertificatesFilesIds: [],
			activities: [],
			city: "",
			country: "",
			registrationNumber: "",
			siren: "",
			streetName: "",
			streetNumber: "",
			postalCode: "",
		},
	});

	const handleSubmit = form.handleSubmit(onSubmit);

	const resetBuilderFormData = useCallback(
		(participantData: ParticipantToSubmissionData) => {
			const activities =
				participantData.totalMissionsOrActivitiesOrMaterialTypes;
			const decennialCertificatesFilesIds = [
				...participantData.participant.filesOnConstructionSiteSubmission
					.filter((file) => file.type === "ATTESTATION_DECENNALE")
					.map((file) => file.file.id),
				...participantData.decennialCertificatesOfSubmissionOrValidFromOthers.map(
					(decennialCertificate) => decennialCertificate.file.id,
				),
			];

			form.reset({
				denomination:
					participantData.participant.society?.denomination ??
					participantData.participant.foreignSociety?.denomination ??
					"",
				totalFeesTTC: participantData.totalFeesTTC,
				totalFeesHT: participantData.totalFeesHT,
				quotesFilesIds:
					participantData.participant.filesOnConstructionSiteSubmission
						.filter((file) => file.type === "DEVIS")
						.map((file) => file.file.id),
				decennialCertificatesFilesIds,
				activities: filterAllowed(activities, builderMainActivities),
				city:
					participantData.participant.society?.headOfficeAddress?.city ?? "",
				streetName:
					participantData.participant.society?.headOfficeAddress?.streetName ??
					"",
				streetNumber:
					participantData.participant.society?.headOfficeAddress
						?.streetNumber ?? "",
				postalCode:
					participantData.participant.society?.headOfficeAddress?.postalCode ??
					"",
				country: participantData.participant.foreignSociety?.country ?? "",
				registrationNumber:
					participantData.participant.foreignSociety?.registrationNumber ?? "",
				siren: participantData.participant.society?.siren ?? undefined,
			});
		},
		[form],
	);

	const resetBuilderFormDataFromReception = useCallback(
		(participantData: SubmissionParticipantWithValidDecennialCertificate) => {
			const activities =
				participantData.totalMissionsOrActivitiesOrMaterialTypes;
			form.reset({
				denomination:
					participantData.participant.society?.denomination ??
					participantData.participant.foreignSociety?.denomination ??
					"",
				totalFeesTTC: participantData.totalFeesTTC ?? 0,
				totalFeesHT: participantData.totalFeesHT ?? 0,
				quotesFilesIds: [],
				decennialCertificatesFilesIds: [],
				activities: filterAllowed(activities, builderMainActivities),
				city:
					participantData.participant.society?.headOfficeAddress?.city ?? "",
				streetName:
					participantData.participant.society?.headOfficeAddress?.streetName ??
					"",
				streetNumber:
					participantData.participant.society?.headOfficeAddress
						?.streetNumber ?? "",
				postalCode:
					participantData.participant.society?.headOfficeAddress?.postalCode ??
					"",
				country: participantData.participant.foreignSociety?.country ?? "",
				registrationNumber:
					participantData.participant.foreignSociety?.registrationNumber ?? "",
				siren: participantData.participant.society?.siren ?? undefined,
			});
		},
		[form],
	);

	useEffect(() => {
		if (shouldFetchParticipant && participantToSubmission) {
			resetBuilderFormData(participantToSubmission);
			return;
		}
		if (prefillFrom) {
			resetBuilderFormDataFromReception(prefillFrom);
		}
	}, [
		shouldFetchParticipant,
		participantToSubmission,
		prefillFrom,
		resetBuilderFormData,
		resetBuilderFormDataFromReception,
	]);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

// Hook pour Material
interface UseMaterialToSubmissionFormProps {
	mode: ParticipantToSubmissionFormMode;
	onSubmit: (data: MaterialToSubmissionFormData) => void;
	constructionSiteId?: number;
	participantId?: number;
	prefillFrom?: SubmissionParticipantWithValidDecennialCertificate | null;
}

export const useMaterialToSubmissionForm = ({
	onSubmit,
	constructionSiteId,
	participantId,
	prefillFrom,
}: UseMaterialToSubmissionFormProps) => {
	const shouldFetchParticipant =
		!prefillFrom && !!constructionSiteId && !!participantId;

	const { data: participantToSubmission } =
		useGetParticipantToConstructionSiteSubmission(
			shouldFetchParticipant ? constructionSiteId : 0,
			shouldFetchParticipant ? participantId : 0,
		);
	const form = useForm({
		resolver: zodResolver(materialToSubmissionFormSchema),
		defaultValues: {
			totalFeesTTC: 0,
			quoteFilesIds: [],
			materialTypes: [],
			totalFeesHT: 0,
		},
	});

	const handleSubmit = form.handleSubmit((data) => onSubmit(data));

	const resetMaterialFormData = useCallback(
		(participantData: ParticipantToSubmissionData) => {
			const materialTypes =
				participantData.totalMissionsOrActivitiesOrMaterialTypes;

			form.reset({
				totalFeesTTC: participantData.totalFeesTTC,
				quoteFilesIds:
					participantData.participant.filesOnConstructionSiteSubmission
						.filter((file) => file.type === "DEVIS")
						.map((file) => file.file.id),
				materialTypes,
				totalFeesHT: participantData.totalFeesHT,
			});
		},
		[form],
	);

	const resetMaterialFormDataFromReception = useCallback(
		(participantData: SubmissionParticipantWithValidDecennialCertificate) => {
			const materialTypes =
				participantData.totalMissionsOrActivitiesOrMaterialTypes;
			form.reset({
				totalFeesTTC: participantData.totalFeesTTC ?? 0,
				quoteFilesIds: [],
				materialTypes,
				totalFeesHT: participantData.totalFeesHT ?? 0,
			});
		},
		[form],
	);

	useEffect(() => {
		if (shouldFetchParticipant && participantToSubmission) {
			resetMaterialFormData(participantToSubmission);
			return;
		}
		if (prefillFrom) {
			resetMaterialFormDataFromReception(prefillFrom);
		}
	}, [
		shouldFetchParticipant,
		participantToSubmission,
		prefillFrom,
		resetMaterialFormData,
		resetMaterialFormDataFromReception,
	]);

	return {
		form,
		handleSubmit,
		isSubmitting: form.formState.isSubmitting,
		errors: form.formState.errors,
	};
};

export const useShouldDisplayHTAmount = (constructionSiteId: number) => {
	const { data: submissionWithOperationDetails } =
		useGetConstructionSiteSubmissionOperation(constructionSiteId);

	const taxMode =
		submissionWithOperationDetails?.constructionSite?.constructionSiteDetails
			?.taxMode;

	const shouldDisplayHT = taxMode === "HT";

	return { shouldDisplayHT };
};
